import { memo, useCallback, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import {
  FiAlertTriangle,
  FiCornerDownRight,
  FiDownload,
  FiUpload,
} from "react-icons/fi";
import DateCell from "../DateCell";
import {
  StepHeader,
  Table,
  Empty,
  Notice,
  byDate,
  StatusPill,
  CountPill,
  rowStyle,
} from "../primitives";
import {
  buildScheduleCsv,
  downloadCsv,
  parseScheduleCsv,
} from "../scheduleCsv";

const cellStyle = { padding: "10px 16px" };
const cellTopStyle = { padding: "10px 16px", verticalAlign: "top" };

/**
 * One row, memoised. The table holds two date pickers per activity; without
 * this, picking a single date re-rendered every picker on the screen and the
 * step felt sluggish. Props are primitives plus one stable callback, so only
 * the edited row re-renders.
 */
const ActivityRow = memo(function ActivityRow({
  id,
  name,
  status,
  startValue,
  endValue,
  originalStart,
  originalEnd,
  locked,
  lockReason,
  dependsOn,
  startProblem,
  endProblem,
  onChange,
}) {
  const problem = startProblem || endProblem;

  // One stable callback per field. Inline arrows here gave both pickers a new
  // onChange on every render, so editing the start date re-rendered the end
  // date's picker (and vice versa) — the open calendar visibly flickered.
  const handleStart = useCallback(
    (date) => onChange(id, "start_date", date),
    [onChange, id]
  );
  const handleEnd = useCallback(
    (date) => onChange(id, "end_date", date),
    [onChange, id]
  );

  return (
    <tr style={rowStyle}>
      <td style={cellStyle}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
          {problem && (
            <FiAlertTriangle
              style={{ color: "#D97706", marginTop: "4px", flexShrink: 0 }}
              size={14}
              title={problem}
            />
          )}
          <div>
            <div style={{ color: "#0d0d0e" }}>{name}</div>
            {dependsOn?.length > 0 && (
              <div
                style={{
                  fontSize: "12px",
                  marginTop: "2px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  color: "#6B7280",
                }}
              >
                <FiCornerDownRight size={11} style={{ flexShrink: 0 }} />
                after {dependsOn.map((d) => d.name || `#${d.id}`).join(", ")}
              </div>
            )}
          </div>
        </div>
      </td>
      <td style={cellTopStyle}>
        <StatusPill status={status} />
      </td>
      <td style={cellTopStyle}>
        {/* Amber marks the field that is actually wrong: the start date for a
            dependency overlap, the end date when it lands before the start. */}
        <DateCell
          value={startValue}
          originalValue={originalStart}
          onChange={handleStart}
          locked={locked}
          lockReason={lockReason}
          conflict={!!startProblem}
        />
      </td>
      <td style={cellTopStyle}>
        <DateCell
          value={endValue}
          originalValue={originalEnd}
          onChange={handleEnd}
          locked={locked}
          lockReason={lockReason}
          conflict={!!endProblem}
        />
      </td>
    </tr>
  );
});

/**
 * Step 1 — activity dates. Everything downstream (billing milestones,
 * contractor payouts, the project window) is derived from what is set here.
 *
 * Dependency conflicts are surfaced but never block: production schedules
 * routinely have activities sharing a date, so refusing to save on an overlap
 * would make the screen unusable. The user decides.
 */
const ActivitiesStep = ({
  schedule,
  editable,
  preview,
  setActivityDrafts,
  activityValue,
}) => {
  const fileInputRef = useRef(null);

  // Sorted on the SERVER dates, not the drafts — otherwise the row being
  // edited jumps position the moment a date is picked.
  const activities = useMemo(
    () => [...(schedule.activities || [])].sort(byDate((a) => a.start_date)),
    [schedule.activities]
  );
  const lockedCount = activities.filter((a) => !a.editable).length;

  // Two things can be wrong with a row, and each points at a different field:
  //   DEPENDENCY_OVERLAP        -> the start date is too early (warning)
  //   ACTIVITY_END_BEFORE_START -> the end date is invalid (blocks the save)
  // Both are shown the same way — amber on the offending field plus an icon —
  // because to the user they are the same thing: this date is wrong.
  const problems = useMemo(() => {
    const start = new Map();
    const end = new Map();

    for (const w of preview?.warnings || []) {
      if (w.code === "DEPENDENCY_OVERLAP") {
        start.set(Number(w.activity_id), w.message);
      }
    }
    for (const b of preview?.blockers || []) {
      if (b.code === "ACTIVITY_END_BEFORE_START") {
        end.set(Number(b.activity_id), b.message);
      }
    }

    return { start, end };
  }, [preview]);

  // Stable across renders (functional setState, no captured deps), so the
  // memoised rows are not invalidated on every keystroke.
  const handleChange = useCallback(
    (id, field, date) =>
      setActivityDrafts((prev) => ({
        ...prev,
        [id]: { ...prev[id], [field]: date },
      })),
    [setActivityDrafts]
  );

  // Exported with the dates currently on screen, drafts included, so a
  // half-finished edit survives a trip through Excel.
  const handleDownloadTemplate = () => {
    const rows = activities.map((activity) => ({
      id: activity.id,
      name: activity.name,
      start_date: activityValue(activity, "start_date"),
      end_date: activityValue(activity, "end_date"),
    }));

    const scopeName = schedule.scope?.name || "scope";
    const slug = scopeName.replace(/[^a-z0-9]+/gi, "-").toLowerCase();

    downloadCsv(`schedule-${slug}.csv`, buildScheduleCsv(rows));
  };

  const handleUpload = (event) => {
    const file = event.target.files?.[0];
    // Reset immediately, so picking the same file twice still fires a change.
    event.target.value = "";
    if (!file) return;

    const reader = new FileReader();

    reader.onerror = () => toast.error("Could not read that file.");
    reader.onload = () => {
      const result = parseScheduleCsv(String(reader.result ?? ""), activities);

      if (result.error) {
        toast.error(result.error);
        return;
      }

      const { drafts, applied, cleared, issues } = result;

      // Merge rather than replace: an upload that covers half the activities
      // must not silently undo dates typed by hand for the other half.
      if (applied > 0) {
        setActivityDrafts((prev) => {
          const next = { ...prev };
          for (const [id, draft] of Object.entries(drafts)) {
            next[id] = { ...next[id], ...draft };
          }
          return next;
        });

        toast.success(
          `${applied} activit${applied === 1 ? "y" : "ies"} updated from the file` +
            `${cleared ? `, ${cleared} date${cleared === 1 ? "" : "s"} cleared` : ""}.`
        );
      } else {
        toast.info("Nothing to update — the file matches what is on screen.");
      }

      // Said out loud rather than swallowed: a row that quietly did nothing is
      // how a schedule ends up half applied without anyone noticing.
      const { locked, unknown, invalidDate, endBeforeStart } = issues;

      if (locked.length) {
        toast.warning(
          `${locked.length} activit${locked.length === 1 ? "y" : "ies"} skipped — ` +
            `already started or delivered: ${locked.slice(0, 3).join(", ")}` +
            `${locked.length > 3 ? "…" : ""}.`
        );
      }
      if (unknown.length) {
        toast.warning(
          `${unknown.length} row${unknown.length === 1 ? "" : "s"} did not match ` +
            `any activity in this scope: ${unknown.slice(0, 3).join(", ")}` +
            `${unknown.length > 3 ? "…" : ""}.`
        );
      }
      if (invalidDate.length) {
        toast.error(
          `${invalidDate.length} date${invalidDate.length === 1 ? "" : "s"} could ` +
            `not be read: ${invalidDate.slice(0, 3).join(", ")}` +
            `${invalidDate.length > 3 ? "…" : ""}. Use DD/MM/YYYY or YYYY-MM-DD.`
        );
      }
      if (endBeforeStart.length) {
        toast.error(
          `${endBeforeStart.length} activit${endBeforeStart.length === 1 ? "y ends" : "ies end"} ` +
            `before they start: ${endBeforeStart.slice(0, 3).join(", ")}` +
            `${endBeforeStart.length > 3 ? "…" : ""}. Fix them below before saving.`
        );
      }
    };

    reader.readAsText(file);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <StepHeader
        title="Activities"
        right={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <CountPill>
              {activities.length} activities
              {lockedCount > 0 && ` · ${lockedCount} locked`}
            </CountPill>

            {activities.length > 0 && (
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "6px 12px",
                    fontSize: "14px",
                    borderRadius: "8px",
                    backgroundColor: "#16A34A",
                    color: "#fff",
                    border: "none",
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                  title="Download the activities with their current dates, fill it in and upload it back"
                >
                  <FiDownload size={14} />
                  Export CSV
                </button>

                {editable && (
                  <>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "6px 12px",
                        fontSize: "14px",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        color: "#374151",
                        backgroundColor: "#fff",
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                      title="Read activity dates from a filled-in template"
                    >
                      <FiUpload size={14} style={{ color: "#4D6BF6" }} />
                      Upload CSV
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,text/csv"
                      onChange={handleUpload}
                      style={{ display: "none" }}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        }
      />

      {lockedCount > 0 && (
        <Notice>
          {lockedCount} of {activities.length} activities have already started
          or been delivered, so their dates stay as they are.
        </Notice>
      )}

      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        {!activities.length ? (
          <Empty>This scope has no activities yet.</Empty>
        ) : (
          <Table columns={["Activity", "Status", "Start", "End"]}>
            {activities.map((activity) => (
              <ActivityRow
                key={activity.id}
                id={activity.id}
                name={activity.name}
                status={activity.status}
                startValue={activityValue(activity, "start_date")}
                endValue={activityValue(activity, "end_date")}
                originalStart={activity.start_date}
                originalEnd={activity.end_date}
                locked={!editable || !activity.editable}
                lockReason={activity.lock_reason}
                dependsOn={activity.depends_on}
                startProblem={problems.start.get(Number(activity.id)) || null}
                endProblem={problems.end.get(Number(activity.id)) || null}
                onChange={handleChange}
              />
            ))}
          </Table>
        )}
      </div>
    </div>
  );
};

export default ActivitiesStep;
